import { Injectable, inject, signal } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from './api.service';
import {
  BugReportClientContext,
  BugReportCreateResponse,
  BugReportInfo,
  BugReportListPage,
  BugReportMessageCreateResponse,
  BugReportMessageInfo,
  BugReportSearchFilters,
  BugReportStatus,
} from '../models';

/** Which side of the API a screen talks to. */
export type BugReportAudience = 'customer' | 'admin';

/**
 * Bug Report Service — frontend wrapper over the two REST controllers
 * exposed by the Symfony backend:
 *
 *   Customer (JWT — own reports only, `customer_id = principal`):
 *   - POST   /api/bug-reports                        (create, multipart)
 *   - GET    /api/bug-reports                        (own list, paginated, ?status=)
 *   - GET    /api/bug-reports/:id                    (own detail; foreign = 404)
 *   - GET    /api/bug-reports/unread-count           ({count} — support messages unread by me)
 *   - GET    /api/bug-reports/:id/messages           (thread, oldest first)
 *   - POST   /api/bug-reports/:id/messages           ({body} → 201 message)
 *   - POST   /api/bug-reports/:id/messages/mark-read (204)
 *
 *   Support (ROLE_SUPPORT):
 *   - GET    /api/support/bug-reports                (admin list, paginated, +?hasUnread=1)
 *   - GET    /api/support/bug-reports/:id            (admin detail)
 *   - PATCH  /api/support/bug-reports/:id            (status update)
 *   - GET/POST /api/support/bug-reports/:id/messages, POST .../mark-read
 *
 * The admin endpoints are gated by ROLE_SUPPORT server-side; ApiService
 * already routes /support/* requests to the parked admin JWT during
 * impersonation, so no extra plumbing is needed here.
 *
 * Note on impersonation: a customer-side message POST made under a support
 * session is stored as a SUPPORT message from the agent (backend rule) — the
 * thread component reads `senderType`, never assumes the endpoint's side.
 *
 * Errors are deliberately NOT swallowed — the caller needs the original
 * HttpErrorResponse to read 422 validation bodies (`{ success: false,
 * errors: { description?, photos?, clientContext? } }`) and surface them
 * inline. Wrap with `catchError(...)` at the call site if appropriate.
 */
@Injectable({
  providedIn: 'root'
})
export class BugReportService {
  private readonly api = inject(ApiService);

  /**
   * Support messages the current user hasn't read, across all their reports
   * — drives the topbar badge. Refreshed by `refreshMyUnreadCount()`; the
   * customer list / thread screens call it after mark-read so the badge
   * drops without waiting for the next poll. Null until first fetched.
   */
  private readonly _myUnreadCount = signal<number | null>(null);
  readonly myUnreadCount = this._myUnreadCount.asReadonly();

  refreshMyUnreadCount(): void {
    this.getMyUnreadCount().subscribe({
      next: n => this._myUnreadCount.set(n),
      error: () => { /* keep the last known value — the badge is best-effort */ },
    });
  }

  /** Forget the cached badge value (logout / session swap). */
  clearMyUnreadCount(): void {
    this._myUnreadCount.set(null);
  }

  /**
   * Submit a new bug report on behalf of the current user. Photos are
   * optional (max 5, jpg/png/gif/webp, 10 MB each — enforced server-side
   * and re-validated here so the request never leaves the browser with
   * obviously bad files). `clientContext` is a JSON-serializable
   * diagnostic snapshot (capped at 64 KB encoded server-side); pass it as
   * an object — the service stringifies into the multipart field.
   *
   * The 201 response carries `photosFailed: string[]` for per-file upload
   * failures that DIDN'T block the report itself — surface them as a
   * non-blocking warning so the user knows some screenshots are missing.
   */
  create(
    description: string,
    photos: File[] = [],
    clientContext?: BugReportClientContext,
    onProgress?: (pct: number) => void,
  ): Observable<BugReportCreateResponse> {
    const formData = new FormData();
    formData.append('description', description);
    for (const file of photos) {
      // Use the bracketed form so PHP's $_FILES collects all uploads under
      // a single key as an array. Without the brackets, PHP keeps only the
      // LAST occurrence — the rest silently never reach Symfony, so the
      // server persists just one photo even if the payload carried three.
      formData.append('photos[]', file);
    }
    if (clientContext !== undefined && clientContext !== null) {
      try {
        formData.append('clientContext', JSON.stringify(clientContext));
      } catch {
        // Circular refs / non-serializable values — drop the snapshot
        // rather than fail the report. Server treats clientContext as
        // best-effort anyway.
      }
    }

    return this.api.upload<BugReportCreateResponse>(
      '/bug-reports',
      formData,
      { onProgress }
    );
  }

  /**
   * Admin list. Supports filtering by status, customer, creationDate range
   * and a sort key (default `-creationDate`). Pagination follows the
   * standard `{ items, pagination }` envelope; `limit` is server-capped
   * and `page` is 1-based.
   *
   * Pass an empty filters object to get the latest reports across all
   * customers — the admin list view's default landing query.
   */
  listForAdmin(
    filters: BugReportSearchFilters = {},
    page?: number,
    limit?: number,
  ): Observable<BugReportListPage> {
    const params: Record<string, string | number> = {};
    if (filters.status !== undefined) params['status'] = filters.status;
    if (filters.customerId !== undefined) params['customerId'] = filters.customerId;
    if (filters.email) params['email'] = filters.email;
    if (filters.from) params['from'] = filters.from;
    if (filters.to) params['to'] = filters.to;
    if (filters.sort) params['sort'] = filters.sort;
    if (filters.hasUnread) params['hasUnread'] = 1;
    if (page !== undefined) params['page'] = page;
    if (limit !== undefined) params['limit'] = limit;

    return this.api.get<BugReportListPage>('/support/bug-reports', { params });
  }

  // ─── Customer area (own reports) ───

  /**
   * The current user's own reports, newest first (`bug_report:list` +
   * thread summary). Ownership is `customer_id = principal`: a report filed
   * during a support session belongs to the AGENT and never shows up in the
   * driven customer's list.
   */
  listMine(status?: BugReportStatus, page?: number, limit?: number): Observable<BugReportListPage> {
    const params: Record<string, string | number> = {};
    if (status !== undefined) params['status'] = status;
    if (page !== undefined) params['page'] = page;
    if (limit !== undefined) params['limit'] = limit;
    return this.api.get<BugReportListPage>('/bug-reports', { params });
  }

  /** Own report with photos (`bug_report:read`). Foreign / deleted → 404. */
  getMine(id: number): Observable<BugReportInfo> {
    return this.api.get<BugReportInfo>(`/bug-reports/${id}`);
  }

  /**
   * `{count}` of support messages unread by the current user, all reports.
   *
   * Unattended background poll: the user never asked for it, so a 401 must
   * not pop the session-expired dialog and a MODULE_*_FORBIDDEN 403 must not
   * raise the permission toast (see ApiCallOptions) — the badge just keeps
   * its last value.
   */
  getMyUnreadCount(): Observable<number> {
    return this.api.get<{ count: number }>('/bug-reports/unread-count', {
      suppressSessionExpiration: true,
      suppressPermissionToast: true,
    }).pipe(
      map(res => Number(res?.count) || 0),
    );
  }

  // ─── Message thread (both sides) ───

  /** Thread, oldest first. Listing does NOT mark anything read. */
  listMessages(audience: BugReportAudience, reportId: number): Observable<BugReportMessageInfo[]> {
    return this.api.get<BugReportMessageInfo[]>(`${this.threadBase(audience, reportId)}`);
  }

  /**
   * Post a message, optionally with photos (max 5, jpg/png/gif/webp, 10 MB
   * each — same rules as the report itself). Sent as multipart when photos
   * are attached, JSON otherwise; the backend accepts both. A message needs
   * text OR at least one photo.
   *
   * Answers the `{message, photosFailed}` envelope: the message is saved even
   * when SOME photos failed, so append it and surface `photosFailed` as a
   * warning. 422 `{error, code}` (`body_required`, `body_too_long`,
   * `too_many_photos`, `photos_failed`) bubbles as an HttpErrorResponse —
   * read `err.error.code`.
   *
   * Customer side: an owner message on a RESOLVED report auto-reopens it
   * (backend rule) — refetch the report afterwards to pick up the status.
   */
  sendMessage(
    audience: BugReportAudience,
    reportId: number,
    body: string,
    photos: File[] = [],
    onProgress?: (pct: number) => void,
  ): Observable<BugReportMessageCreateResponse> {
    const url = this.threadBase(audience, reportId);
    if (photos.length === 0) {
      return this.api.post<BugReportMessageCreateResponse>(url, { body });
    }
    const formData = new FormData();
    formData.append('body', body);
    for (const file of photos) {
      // Bracketed name so PHP collects every upload under one key as an array
      // (without it only the LAST file reaches Symfony) — same as report create.
      formData.append('photos[]', file);
    }
    return this.api.upload<BugReportMessageCreateResponse>(url, formData, { onProgress });
  }

  /**
   * Stamp the OTHER side's messages as read (204). Pass the ids of the
   * messages actually rendered so a reply that lands after your GET isn't
   * silently acknowledged; with no ids the backend marks every unread one.
   * Under support impersonation the customer-side call is a server no-op.
   */
  markMessagesRead(audience: BugReportAudience, reportId: number, messageIds?: number[]): Observable<void> {
    const body = messageIds && messageIds.length > 0 ? { messageIds } : {};
    return this.api.post<void>(`${this.threadBase(audience, reportId)}/mark-read`, body);
  }

  private threadBase(audience: BugReportAudience, reportId: number): string {
    return audience === 'admin'
      ? `/support/bug-reports/${reportId}/messages`
      : `/bug-reports/${reportId}/messages`;
  }

  /**
   * Admin detail. Returns the full `bug_report:read + bug_report:admin`
   * shape — photos[], photoCount, clientContext, customerEmail, customerName.
   * 404 maps to a NOT_FOUND HttpErrorResponse — let it bubble.
   */
  getForAdmin(id: number): Observable<BugReportInfo> {
    return this.api.get<BugReportInfo>(`/support/bug-reports/${id}`);
  }

  /**
   * Toggle a report between OPEN and RESOLVED. Server stamps
   * `resolvedDate` on first transition to RESOLVED and clears it on
   * reopen — no client bookkeeping needed. Returns the updated report
   * with the same `bug_report:read + bug_report:admin` shape as the
   * detail endpoint.
   */
  updateStatus(id: number, status: BugReportStatus): Observable<BugReportInfo> {
    return this.api.patch<BugReportInfo>(`/support/bug-reports/${id}`, { status });
  }
}
